import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useAppSelector } from './redux';
import { websocketService } from '../services/websocketService';

/**
 * WebSocket Connection Hook
 * 
 * @description
 * Provides a React interface to manage WebSocket connections
 * with automatic cleanup and connection state management
 * 
 * @param {string} [url] - Optional WebSocket server URL
 * @returns {object} WebSocket connection interface
 * 
 * @example
 * ```tsx
 * const { connect, disconnect, isConnected, subscribeToChannel } = useWebSocket();
 * 
 * useEffect(() => {
 *   connect('ws://localhost:3000');
 *   return () => disconnect();
 * }, []);
 * ```
 */
export const useWebSocket = (url?: string) => {
  // Connection state from Redux store
  const connection = useAppSelector(
    (state) =>
      state.websocket?.connection ?? {
        isConnected: false,
        clientId: null,
        error: null,
        reconnectAttempts: 0,
      }
  );

  // Refs to prevent unnecessary re-renders and track state
  const isConnectingRef = useRef(false);
  const currentUrlRef = useRef<string | undefined>(url);
  const mountedRef = useRef(true);
  const connectionAttemptRef = useRef(0);

  // Update URL reference when it changes
  useEffect(() => {
    currentUrlRef.current = url;
  }, [url]);

  // Mark component as mounted/unmounted
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Connect to WebSocket server
   * Implements connection guard to prevent duplicate connections
   * 
   * @param {string} [connectUrl] - Override URL for this connection attempt
   * @throws {Error} When URL is not provided or connection fails
   */
  const connect = useCallback(
    async (connectUrl?: string) => {
      const targetUrl = connectUrl || currentUrlRef.current;

      // Guard: Prevent duplicate connections
      if (isConnectingRef.current || connection.isConnected) {
        console.debug('[useWebSocket] Connection already in progress or established');
        return;
      }

      // Guard: Validate URL
      if (!targetUrl) {
        const error = new Error('WebSocket URL is required');
        console.error('[useWebSocket]', error.message);
        throw error;
      }

      // Guard: Check if component is still mounted
      if (!mountedRef.current) {
        console.debug('[useWebSocket] Component unmounted, aborting connection');
        return;
      }

      isConnectingRef.current = true;
      connectionAttemptRef.current++;

      try {
        console.info('[useWebSocket] Initiating connection to:', targetUrl);
        await websocketService.connect(targetUrl);
        
        if (mountedRef.current) {
          console.info('[useWebSocket] Successfully connected');
          connectionAttemptRef.current = 0; // Reset on success
        }
      } catch (error) {
        if (mountedRef.current) {
          console.error('[useWebSocket] Connection failed:', error);
        }
        throw error;
      } finally {
        if (mountedRef.current) {
          isConnectingRef.current = false;
        }
      }
    },
    [connection.isConnected]
  );

  /**
   * Disconnect from WebSocket server
   * Cleans up connection and resets state
   */
  const disconnect = useCallback(() => {
    console.info('[useWebSocket] Disconnecting...');
    isConnectingRef.current = false;
    connectionAttemptRef.current = 0;
    websocketService.disconnect();
  }, []);

  /**
   * Subscribe to a WebSocket channel
   * Only works when connected
   * 
   * @param {string} channel - Channel name to subscribe to
   */
  const subscribeToChannel = useCallback(
    (channel: string) => {
      if (!connection.isConnected) {
        console.warn(`[useWebSocket] Cannot subscribe to ${channel}: not connected`);
        return;
      }

      if (!channel || typeof channel !== 'string') {
        console.error('[useWebSocket] Invalid channel name:', channel);
        return;
      }

      console.debug('[useWebSocket] Subscribing to channel:', channel);
      websocketService.subscribeToChannel(channel);
    },
    [connection.isConnected]
  );

  /**
   * Unsubscribe from a WebSocket channel
   * 
   * @param {string} channel - Channel name to unsubscribe from
   */
  const unsubscribeFromChannel = useCallback(
    (channel: string) => {
      if (!connection.isConnected) {
        console.warn(`[useWebSocket] Cannot unsubscribe from ${channel}: not connected`);
        return;
      }

      if (!channel || typeof channel !== 'string') {
        console.error('[useWebSocket] Invalid channel name:', channel);
        return;
      }

      console.debug('[useWebSocket] Unsubscribing from channel:', channel);
      websocketService.unsubscribeFromChannel(channel);
    },
    [connection.isConnected]
  );

  /**
   * Get information about a specific channel
   * 
   * @param {string} channel - Channel name
   */
  const getChannelInfo = useCallback(
    (channel: string) => {
      if (!connection.isConnected) {
        console.warn(`[useWebSocket] Cannot get channel info for ${channel}: not connected`);
        return;
      }

      websocketService.getChannelInfo(channel);
    },
    [connection.isConnected]
  );

  /**
   * Send ping to server for connection health check
   */
  const ping = useCallback(() => {
    if (!connection.isConnected) {
      console.warn('[useWebSocket] Cannot ping: not connected');
      return;
    }

    websocketService.ping();
  }, [connection.isConnected]);

  /**
   * Get connection statistics
   */
  const getConnectionStats = useCallback(() => {
    return websocketService.getConnectionStats();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      isConnectingRef.current = false;
      connectionAttemptRef.current = 0;
      
      // Disconnect when component unmounts
      // Note: This will affect all components using this hook
      // Consider implementing a ref counter if needed
      console.debug('[useWebSocket] Component unmounting, cleaning up');
    };
  }, []);

  // Memoize return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // State
      connection,
      isConnected: connection.isConnected,
      isConnecting: isConnectingRef.current,
      error: connection.error,
      clientId: connection.clientId,
      reconnectAttempts: connection.reconnectAttempts,

      // Actions
      connect,
      disconnect,
      subscribeToChannel,
      unsubscribeFromChannel,
      getChannelInfo,
      ping,
      getConnectionStats,
    }),
    [
      connection,
      connect,
      disconnect,
      subscribeToChannel,
      unsubscribeFromChannel,
      getChannelInfo,
      ping,
      getConnectionStats,
    ]
  );
};

/**
 * Type exports for TypeScript support
 */
export type UseWebSocketReturn = ReturnType<typeof useWebSocket>;
